/**
 * The register's write sequence, lifted out of the screen.
 *
 * It lives here rather than inside `Register.tsx` for one reason: this is the
 * only sequence in the dashboard where **the order of two writes is the
 * difference between a correct kitchen ticket and a wrong one**, and a sequence
 * nothing can drive is a sequence nothing can prove. Nothing below touches
 * React, so `registerFlow.test.ts` runs it against the real outbox.
 *
 * `Tarabeeza-POS-App/src/lib/register-flow.ts` is the same rule on the till
 * hardware, and it exists because of a production bug. This is its dashboard
 * twin, sharing its invariant and its vocabulary on purpose — two clients that
 * disagree about when the kitchen may be told is exactly the bug being guarded
 * against.
 *
 * The rule the whole file exists to hold:
 *
 * > **An order is never handed to the kitchen while any of its lines are still
 * > sitting in this browser.**
 *
 * `POST /orders/:id/send` routes the order and closes it to further edits. Fire
 * it while `POST /orders/:id/items` is still queued and three things happen at
 * once: the kitchen gets a ticket with lines missing, the queued add is then
 * refused by `assertEditable` and dead-lettered, and the cashier — who was
 * shown "Sent to the kitchen" — has no cart left to re-ring from. Money and
 * food both go wrong, silently.
 *
 * ## Which path each write takes
 *
 * All three go through `outbox.submit()`, never `outbox.mutate()`, and the
 * difference is the whole point. `mutate()` sends **its own entry** and
 * deliberately does not drain the queue behind it — right for a write that
 * stands alone, and wrong for these, because `create → add items → send` are
 * only true in that order. `submit()` drains the queue in order and stops at
 * the first entry that cannot go, so a later step physically cannot land before
 * an earlier one. `@/lib/mutations` is where all three are declared.
 *
 * And ordering alone is not enough. A step that did not land **parks**, and the
 * step after it is not made — not even queued behind it. Queueing the send
 * *would* be ordered, but if the add ever dead-lettered, the send would become
 * the head of the queue and fire on its own, which is the same disaster one
 * flush later. The cashier presses Send again instead, and that press is a
 * drain of the entry that already exists rather than a second write.
 */
import {
  locateWrite,
  mutations,
  resolveHeld,
  type CreateOrderInput,
  type PlacedOrder,
} from '@/lib/mutations';
import { outbox } from '@/lib/outbox';
import { refusedLine } from '@/lib/orderRefusal';
import { orderLines, type CartLine } from './registerCart';

// ---------------------------------------------------------------- the target

/**
 * What the order is being composed *against* — ADR-003's fulfillment context.
 *
 * Party is gone. A bill reaches a table through the table's open `Session`
 * (`Check.sessionId`), and a bill with no table at all — a kids room, a console
 * lounge, a garden bench — is reached as itself, which is also what a resource
 * session holds (`ResourceSession.checkId`, pinned when the session starts, and
 * `ResourceSession.tableId` when the console is being run as part of a table).
 * So the three targets below are the three the API actually accepts, and none
 * of them is a party.
 *
 * This is deliberately not `Spaces/lib/order-target.ts`. That one is built
 * around a `LiveTable` off the floor map and can only express DINE_IN; a
 * register picks an open *visit* it did not draw and has to be able to say
 * TAKEAWAY, which is a different question with a different answer.
 */
export type RegisterTarget =
  /** An open visit. The server routes the charge from the session itself. */
  | { kind: 'session'; sessionId: string }
  /** A bag over the counter. No session; the branch is the only context. */
  | { kind: 'takeaway'; customerName: string }
  /** The bill itself is the context — a resource session's check, or a tab. */
  | { kind: 'check'; checkId: string };

export interface OrderContext {
  /** Sent because a takeaway order has no session to take a branch from. */
  branchId: string | null;
  target: RegisterTarget;
}

/**
 * The `POST /orders` body.
 *
 * The API runs a global `ValidationPipe` with `forbidNonWhitelisted`, so a key
 * `CreateOrderDto` does not declare is a 400 — and a 400 dead-letters the
 * entry. Every field below is one it declares, and an absent id is omitted
 * rather than sent as null.
 */
export function orderBodyFor(
  context: OrderContext,
  cart: CartLine[],
  submit: boolean,
): CreateOrderInput {
  const { target } = context;
  return {
    ...(target.kind === 'session' ? { sessionId: target.sessionId } : {}),
    ...(target.kind === 'check' ? { checkId: target.checkId } : {}),
    ...(context.branchId ? { branchId: context.branchId } : {}),
    ...(target.kind === 'takeaway' && target.customerName.trim()
      ? { customerName: target.customerName.trim() }
      : {}),
    fulfillmentType: target.kind === 'takeaway' ? 'TAKEAWAY' : 'DINE_IN',
    items: orderLines(cart),
    ...(submit ? { submit: true } : {}),
  };
}

// ----------------------------------------------------------------- the state

/**
 * A write that is durably on this browser's disk and has not been acknowledged.
 *
 * It is identified by its `operationId` and nothing else: that is the replay
 * key, so resolving it later is a drain of the entry that already exists rather
 * than a second write of the same intent.
 */
export interface HeldWrite {
  kind: 'create' | 'add' | 'send';
  /** Whether landing this entry also hands the ticket to the kitchen. */
  sends: boolean;
  operationId: string;
}

export interface RegisterState {
  order: PlacedOrder | null;
  cart: CartLine[];
  held: HeldWrite | null;
}

/** Which cache families the caller should invalidate. Never on a held write. */
export type CacheEffect = 'order' | 'checks' | 'sessions';

export interface Notice {
  key: string;
  /** Interpolation for the one notice that quotes the server back. */
  values?: Record<string, string>;
}

/**
 * A 422 the cashier is expected to fix, here and now.
 *
 * Reported apart from {@link Notice} because it is not an error and must not be
 * drawn as one: the cart is intact, the operator's next move is to swap one
 * line, and its dead letter has already been taken out of the queue — leaving
 * it would park a "1 failed action" badge over work that was never at risk.
 */
export interface Refusal {
  /** The server's own sentence. It names the item, and for a schedule refusal
   *  the menu and the hours it *is* served on. Worth more than a generic line. */
  message: string;
  /** The cart line it is about, when it can be matched. */
  lineKey: string | null;
}

export interface RegisterResult {
  state: RegisterState;
  notice: Notice | null;
  refusal: Refusal | null;
  effects: CacheEffect[];
}

const notice = (key: string, values?: Record<string, string>): Notice => ({ key, values });

/** A sent order opens or joins a check, and seats the session it is on. */
const SENT: CacheEffect[] = ['order', 'checks', 'sessions'];

/**
 * Whether the cart is showing lines the server has not accepted.
 *
 * While it is, the cart is read-only. The lines in it are already committed —
 * they are on disk under a replay key and are going to land — so editing them
 * would leave the queue and the screen describing two different orders, and
 * re-ringing them would add them twice. A queued *send* carries no cart lines
 * with it, so it does not lock anything.
 */
export const cartIsHeld = (held: HeldWrite | null): boolean =>
  held !== null && held.kind !== 'send';

const heldNotice = (held: HeldWrite): Notice =>
  notice(held.sends ? 'billing.register.heldForKitchen' : 'billing.register.heldOnTill');

/**
 * A 4xx during the flush, turned into something the cashier can act on.
 *
 * A 422 is the refusal a cached menu makes routine and is handled where the
 * cart is: the letter is discarded — the operator is looking at the problem, so
 * there is nothing for the sync banner to chase — and the message comes back
 * inline. Anything else genuinely is a failed write and stays in the dead
 * letter, where `SyncBanner` shows it.
 */
async function refusalFor(
  operationId: string,
  status: number | null,
  message: string,
  cart: CartLine[],
): Promise<{ refusal: Refusal | null; notice: Notice | null }> {
  if (status !== 422) return { refusal: null, notice: notice('billing.register.writeRefused', { message }) };
  await outbox.discardDead(operationId);
  return {
    refusal: { message, lineKey: refusedLine(message, cart)?.key ?? null },
    notice: null,
  };
}

/**
 * A write came back unacknowledged. Say which kind of unacknowledged it is
 * before telling the cashier anything: a 4xx during the flush is already in the
 * dead letter, and "saved, it will send" would be a lie about it.
 */
async function park(
  state: RegisterState,
  held: HeldWrite,
  effects: CacheEffect[] = [],
): Promise<RegisterResult> {
  const where = await locateWrite(held.operationId);
  if (where.settled === 'refused') {
    // The cart keeps the lines and unlocks, so the cashier can fix and re-ring.
    const { refusal, notice: line } = await refusalFor(
      where.operationId,
      where.status,
      where.message,
      state.cart,
    );
    return { state: { ...state, held: null }, notice: line, refusal, effects };
  }
  // A background flush may have delivered it between the press and this
  // question. Reading that as still-waiting is safe and self-correcting: the
  // next press resolves it properly, and nothing has been claimed that is not
  // true.
  return { state: { ...state, held }, notice: heldNotice(held), refusal: null, effects };
}

type Resumed = { done: true; result: RegisterResult } | { done: false; state: RegisterState };

/**
 * The cashier pressed again while something is held. Drain, and continue from
 * wherever the queue actually got to.
 */
async function resume(state: RegisterState, held: HeldWrite): Promise<Resumed> {
  const where = await resolveHeld<PlacedOrder>(held.operationId);

  if (where.settled === 'waiting') {
    return {
      done: true,
      result: { state, notice: heldNotice(held), refusal: null, effects: [] },
    };
  }

  if (where.settled === 'refused') {
    const { refusal, notice: line } = await refusalFor(
      where.operationId,
      where.status,
      where.message,
      state.cart,
    );
    return {
      done: true,
      result: { state: { ...state, held: null }, notice: line, refusal, effects: [] },
    };
  }

  // Delivered. `data` is absent only when a background flush was the one that
  // sent it, in which case the row on screen is the best copy we have and the
  // next read refreshes it.
  const order = where.data ?? state.order;
  // A held create or add carried the whole cart, so landing it is what empties
  // the cart — never the press that queued it. A held send carried no lines, so
  // whatever is in the cart was rung up afterwards and still has to go.
  const cart = held.kind === 'send' ? state.cart : [];
  const next: RegisterState = { order, cart, held: null };

  if (held.sends && !cart.length) {
    return {
      done: true,
      result: {
        state: next,
        notice: notice('billing.register.orderSent'),
        refusal: null,
        effects: SENT,
      },
    };
  }
  return { done: false, state: next };
}

/** Hold: park the lines on a DRAFT order so the cashier can keep adding. */
export async function holdCart(
  state: RegisterState,
  context: OrderContext,
): Promise<RegisterResult> {
  let landed: Notice | null = null;

  if (state.held) {
    const resumed = await resume(state, state.held);
    if (resumed.done) return resumed.result;
    state = resumed.state;
    landed = notice(
      state.order ? 'billing.register.linesAdded' : 'billing.register.orderHeld',
    );
  }

  if (!state.cart.length) {
    return landed
      ? { state, notice: landed, refusal: null, effects: ['order'] }
      : { state, notice: null, refusal: null, effects: [] };
  }

  if (!state.order) {
    const created = await mutations.createOrder(orderBodyFor(context, state.cart, false));
    if (!created.synced || !created.data) {
      return park(state, { kind: 'create', sends: false, operationId: created.operationId });
    }
    return {
      state: { order: created.data, cart: [], held: null },
      notice: notice('billing.register.orderHeld'),
      refusal: null,
      effects: ['order'],
    };
  }

  const added = await mutations.addOrderItems(state.order.id, orderLines(state.cart));
  if (!added.synced || !added.data) {
    return park(state, { kind: 'add', sends: false, operationId: added.operationId });
  }
  return {
    state: { order: added.data, cart: [], held: null },
    notice: notice('billing.register.linesAdded'),
    refusal: null,
    effects: ['order'],
  };
}

/**
 * Send: create-and-fire in one call when there is no order yet — that keeps it
 * a single outbox entry, which is what a register on a venue's back-office
 * Wi-Fi needs — otherwise push the new lines onto the open order and re-send.
 * Re-sending is additive server-side, so already-routed lines are not charged
 * twice.
 */
export async function sendCart(
  state: RegisterState,
  context: OrderContext,
): Promise<RegisterResult> {
  if (state.held) {
    const resumed = await resume(state, state.held);
    if (resumed.done) return resumed.result;
    state = resumed.state;
  }

  if (!state.order && !state.cart.length) {
    return { state, notice: null, refusal: null, effects: [] };
  }

  if (!state.order) {
    const created = await mutations.createOrder(orderBodyFor(context, state.cart, true));
    if (!created.synced || !created.data) {
      // One entry that both creates and fires, so there is no ordering to get
      // wrong and nothing to hold back: it reaches the kitchen when it lands.
      return park(state, { kind: 'create', sends: true, operationId: created.operationId });
    }
    return {
      state: { order: created.data, cart: [], held: null },
      notice: notice('billing.register.orderSent'),
      refusal: null,
      effects: SENT,
    };
  }

  let open = state.order;

  if (state.cart.length) {
    const added = await mutations.addOrderItems(open.id, orderLines(state.cart));
    if (!added.synced || !added.data) {
      // The lines have not been accepted. `sendOrder` is NOT called — see the
      // rule at the top of this file. The cart keeps them and the notice says
      // plainly that the kitchen has not been told.
      return park(state, { kind: 'add', sends: false, operationId: added.operationId });
    }
    open = added.data;
    state = { order: open, cart: [], held: null };
  }

  const sent = await mutations.sendOrder(open.id);
  if (!sent.synced || !sent.data) {
    // Safe to leave queued: every line is already on the order, so this entry
    // has nothing it can overtake. The order cache still changed if lines just
    // landed, which is why the effect is kept.
    return park(state, { kind: 'send', sends: true, operationId: sent.operationId }, ['order']);
  }
  return {
    state: { order: sent.data, cart: [], held: null },
    notice: notice('billing.register.orderSent'),
    refusal: null,
    effects: SENT,
  };
}
